/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { Injectable } from '@angular/core';
import { createMachine, interpret } from '@xstate/fsm';
import { Subject } from 'rxjs';

/**
 * Allowed share status values.
 */
export enum ShareStatus {
  SHARED = 'shared',
  MODIFIED = 'modified',
  PRIVATE = 'private',
}

/**
 * Allowed share status transitions.
 */
export enum ShareTransition {
  SHARE = 'SHARE',
  EDIT = 'EDIT',
  MAKE_PRIVATE = 'MAKE_PRIVATE',
}

/**
 * Service to store/broadcast data/statuses related to the unit module.
 */
@Injectable({
  providedIn: 'root',
})
export class UnitStateStore {

  /**
   * Observable for subscribing to share status changes.
   */
  public shareStatus$ : Subject<ShareStatus>;

  private shareStatusStateMachine;

  /**
   * Default Constructor.
   */
  constructor() {
    this.initShareStatus();
  }

  /**
   * Initialize the share status state machine.
   * @private
   */
  private initShareStatus() {
    const shareStatusMachine = createMachine({
      id: 'shareStatus',
      initial: 'private',
      states: {
        private: {
          on: {
            SHARE: 'shared',
            RESET_MODIFIED: 'modified',
            RESET_PRIVATE: 'private',
            RESET_SHARED: 'shared',
          },
        },
        shared: {
          on: {
            EDIT: 'modified',
            MAKE_PRIVATE: 'private',
            RESET_MODIFIED: 'modified',
            RESET_PRIVATE: 'private',
            RESET_SHARED: 'shared',
          },
        },
        modified: {
          on: {
            SHARE: 'shared',
            MAKE_PRIVATE: 'private',
            RESET_MODIFIED: 'modified',
            RESET_PRIVATE: 'private',
            RESET_SHARED: 'shared',
          },
        },
      },
    });

    this.shareStatusStateMachine = interpret(shareStatusMachine);

    this.shareStatus$ = new Subject();
    this.shareStatusStateMachine.subscribe((state) => {
      if (state.changed) {
        this.shareStatus$.next(state.value);
      }
    });

    this.shareStatusStateMachine.start();
  }

  /**
   * Transition the share status to a new state.
   * @param transition Transition for the share status.
   */
  public transitionShareStatus(transition : ShareTransition) {
    this.shareStatusStateMachine.send(transition);
  }

  /**
   * Set the shareStatus to a specific value, ignoring transitions.  Used when loading a new unit.
   * @param status Value to set the shareStatus to.
   */
  public setShareStatus(status : ShareStatus) {
    this.shareStatusStateMachine.send(`RESET_${status.toUpperCase()}`);
  }

  /**
   * return the current share status.
   */
  public getShareStatus() : ShareStatus {
    return this.shareStatusStateMachine.state.value;
  }

  // eslint-disable-next-line class-methods-use-this
  public getShareStatusEnum() {
    return ShareStatus;
  }

  // eslint-disable-next-line class-methods-use-this
  public getShareTransitionEnum() {
    return ShareTransition;
  }

}
